import React, { useState } from 'react';
import { Database, Copy, Trash2, ArrowRight } from 'lucide-react';

const SqlToMongoTool = () => {
  const [sql, setSql] = useState('');
  const [mongo, setMongo] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const convertSqlToMongo = () => {
    setError('');
    setMongo('');

    if (!sql.trim()) {
      setError('Please enter a SQL query');
      return;
    }

    try {
      const query = sql.trim().replace(/\s+/g, ' ');
      let result = '';

      // SELECT query
      if (/^SELECT/i.test(query)) {
        result = convertSelect(query);
      }
      // INSERT query
      else if (/^INSERT/i.test(query)) {
        result = convertInsert(query);
      }
      // UPDATE query
      else if (/^UPDATE/i.test(query)) {
        result = convertUpdate(query);
      }
      // DELETE query
      else if (/^DELETE/i.test(query)) {
        result = convertDelete(query);
      }
      else {
        throw new Error('Unsupported SQL command. Supported: SELECT, INSERT, UPDATE, DELETE');
      }

      setMongo(result);
    } catch (e) {
      setError(e.message);
    }
  };

  const convertSelect = (query) => {
    const selectMatch = query.match(/SELECT\s+(.+?)\s+FROM\s+(\w+)(?:\s+WHERE\s+(.+?))?(?:\s+ORDER\s+BY\s+(.+?))?(?:\s+LIMIT\s+(\d+))?(?:\s+OFFSET\s+(\d+))?$/i);
    
    if (!selectMatch) throw new Error('Invalid SELECT syntax');

    const [, fields, table, where, orderBy, limit, offset] = selectMatch;
    
    // Build projection
    let projection = {};
    if (fields.trim() !== '*') {
      fields.split(',').forEach(f => {
        const field = f.trim();
        projection[field] = 1;
      });
    }

    // Build filter
    let filter = {};
    if (where) {
      filter = parseWhereClause(where);
    }

    // Build sort
    let sort = {};
    if (orderBy) {
      orderBy.split(',').forEach(o => {
        const [field, dir] = o.trim().split(/\s+/);
        sort[field] = dir?.toUpperCase() === 'DESC' ? -1 : 1;
      });
    }

    // Build MongoDB query
    let mongoQuery = `db.${table}.find(`;
    mongoQuery += JSON.stringify(filter, null, 2);
    
    if (Object.keys(projection).length > 0) {
      mongoQuery += `, ${JSON.stringify(projection, null, 2)}`;
    }
    mongoQuery += ')';

    if (Object.keys(sort).length > 0) {
      mongoQuery += `.sort(${JSON.stringify(sort)})`;
    }
    if (offset) {
      mongoQuery += `.skip(${offset})`;
    }
    if (limit) {
      mongoQuery += `.limit(${limit})`;
    }

    return mongoQuery;
  };

  const parseWhereClause = (where) => {
    const filter = {};
    
    // Handle AND conditions
    const andConditions = where.split(/\s+AND\s+/i);
    
    andConditions.forEach(cond => {
      // Parse individual condition
      const match = cond.match(/(\w+)\s*(=|!=|<>|>|<|>=|<=|LIKE|IN|IS NULL|IS NOT NULL)\s*(.+)?/i);
      if (!match) return;

      const [, field, operator, value] = match;
      const cleanValue = value?.replace(/^['"]|['"]$/g, '').trim();

      switch (operator.toUpperCase()) {
        case '=':
          filter[field] = isNaN(cleanValue) ? cleanValue : Number(cleanValue);
          break;
        case '!=':
        case '<>':
          filter[field] = { $ne: isNaN(cleanValue) ? cleanValue : Number(cleanValue) };
          break;
        case '>':
          filter[field] = { $gt: Number(cleanValue) };
          break;
        case '<':
          filter[field] = { $lt: Number(cleanValue) };
          break;
        case '>=':
          filter[field] = { $gte: Number(cleanValue) };
          break;
        case '<=':
          filter[field] = { $lte: Number(cleanValue) };
          break;
        case 'LIKE':
          const pattern = cleanValue.replace(/%/g, '.*');
          filter[field] = { $regex: pattern, $options: 'i' };
          break;
        case 'IN':
          const values = cleanValue.replace(/[()]/g, '').split(',').map(v => v.trim().replace(/^['"]|['"]$/g, ''));
          filter[field] = { $in: values };
          break;
        case 'IS NULL':
          filter[field] = null;
          break;
        case 'IS NOT NULL':
          filter[field] = { $ne: null };
          break;
      }
    });

    return filter;
  };

  const convertInsert = (query) => {
    const match = query.match(/INSERT\s+INTO\s+(\w+)\s*\((.+?)\)\s*VALUES\s*\((.+?)\)/i);
    if (!match) throw new Error('Invalid INSERT syntax');

    const [, table, columns, values] = match;
    const cols = columns.split(',').map(c => c.trim());
    const vals = values.split(',').map(v => {
      const clean = v.trim().replace(/^['"]|['"]$/g, '');
      return isNaN(clean) ? clean : Number(clean);
    });

    const doc = {};
    cols.forEach((col, i) => {
      doc[col] = vals[i];
    });

    return `db.${table}.insertOne(${JSON.stringify(doc, null, 2)})`;
  };

  const convertUpdate = (query) => {
    const match = query.match(/UPDATE\s+(\w+)\s+SET\s+(.+?)\s+WHERE\s+(.+)/i);
    if (!match) throw new Error('Invalid UPDATE syntax');

    const [, table, setClause, where] = match;
    
    const update = { $set: {} };
    setClause.split(',').forEach(s => {
      const [field, value] = s.split('=').map(x => x.trim());
      const cleanValue = value.replace(/^['"]|['"]$/g, '');
      update.$set[field] = isNaN(cleanValue) ? cleanValue : Number(cleanValue);
    });

    const filter = parseWhereClause(where);

    return `db.${table}.updateMany(\n  ${JSON.stringify(filter, null, 2)},\n  ${JSON.stringify(update, null, 2)}\n)`;
  };

  const convertDelete = (query) => {
    const match = query.match(/DELETE\s+FROM\s+(\w+)\s+WHERE\s+(.+)/i);
    if (!match) throw new Error('Invalid DELETE syntax');

    const [, table, where] = match;
    const filter = parseWhereClause(where);

    return `db.${table}.deleteMany(${JSON.stringify(filter, null, 2)})`;
  };

  const copyResult = () => {
    navigator.clipboard.writeText(mongo);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const examples = [
    "SELECT * FROM users WHERE age > 25",
    "SELECT name, email FROM users WHERE status = 'active' ORDER BY name LIMIT 10",
    "INSERT INTO users (name, email, age) VALUES ('John', 'john@email.com', 30)",
    "UPDATE users SET status = 'inactive' WHERE last_login < '2024-01-01'",
    "DELETE FROM users WHERE status = 'deleted'"
  ];

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">SQL Query</label>
          <textarea
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            placeholder="SELECT * FROM users WHERE age > 25"
            className="w-full h-48 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-sm placeholder-gray-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">MongoDB Query</label>
          <div className="w-full h-48 bg-gray-800 border border-gray-700 rounded-lg p-4 overflow-auto">
            {error ? (
              <div className="text-red-400 text-sm">{error}</div>
            ) : mongo ? (
              <pre className="text-green-400 text-sm whitespace-pre-wrap">{mongo}</pre>
            ) : (
              <span className="text-gray-500 text-sm">MongoDB query will appear here...</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={convertSqlToMongo}
          disabled={!sql.trim()}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
        >
          <ArrowRight size={18} />
          Convert to MongoDB
        </button>
        <button
          onClick={copyResult}
          disabled={!mongo}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <button
          onClick={() => { setSql(''); setMongo(''); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear
        </button>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Example SQL Queries</h3>
        <div className="space-y-2">
          {examples.map((ex, i) => (
            <button
              key={i}
              onClick={() => setSql(ex)}
              className="block w-full text-left px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm text-gray-300 font-mono transition-colors"
            >
              {ex}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
        <h3 className="text-sm font-medium text-blue-300 mb-2">Supported Features</h3>
        <ul className="text-sm text-gray-400 space-y-1">
          <li>• SELECT with WHERE, ORDER BY, LIMIT, OFFSET</li>
          <li>• Operators: =, !=, &lt;, &gt;, &lt;=, &gt;=, LIKE, IN, IS NULL</li>
          <li>• INSERT INTO with values</li>
          <li>• UPDATE with SET and WHERE</li>
          <li>• DELETE with WHERE</li>
        </ul>
      </div>
    </div>
  );
};

export default SqlToMongoTool;
